import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Scan, Camera, Keyboard, X, CheckCircle2, AlertCircle, Plus } from "lucide-react";
import ProductHoverPreview from "../../components/ProductHoverPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Быстрая инвентаризация — сканирование этикеток товара.
 *
 * Два режима:
 *   • USB-сканер (default): большое поле <input autoFocus>. Сканер печатает
 *     штрихкод + Enter, обработчик onKeyDown ловит Enter и делает запрос.
 *   • Камера: включает камеру через `html5-qrcode`, распознаёт EAN-13 / Code-128.
 *
 * После скана:
 *   ✅ найдено — карточка товара + остаток по складам
 *   ⚠️ не найдено — предложение создать товар с этим кодом
 *
 * Внизу — история последних 30 сканирований.
 */
export default function AdminInventoryAudit() {
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("usb"); // "usb" | "camera"
  const [result, setResult] = useState(null); // { found, product, stock, total_qty, code }
  const [scanning, setScanning] = useState(false);
  const [history, setHistory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const inputRef = useRef(null);
  const cameraDivId = "html5qr-scanner-div";
  const scannerRef = useRef(null);

  // Load warehouses + recent history on mount
  useEffect(() => {
    loadHistory();
    axios.get(`${API}/admin/warehouses`, { withCredentials: true })
      .then(r => {
        setWarehouses(r.data || []);
        const def = (r.data || []).find(w => w.is_default) || (r.data || [])[0];
        if (def) setWarehouseId(def.id);
      }).catch(() => {});
  }, []);

  // Keep the scanner input focused when in USB mode
  useEffect(() => {
    if (mode === "usb") inputRef.current?.focus();
  }, [mode, result]);

  const loadHistory = () => {
    axios.get(`${API}/admin/inventory/audit-scans?limit=30`, { withCredentials: true })
      .then(r => setHistory(r.data || []))
      .catch(() => {});
  };

  const doLookup = async (raw) => {
    const c = (raw || "").trim();
    if (!c) return;
    setScanning(true);
    try {
      const r = await axios.get(`${API}/admin/inventory/lookup`, {
        params: { code: c }, withCredentials: true,
      });
      setResult(r.data);
      // Log the audit trail (fire-and-forget)
      axios.post(`${API}/admin/inventory/audit-scan`, {
        code: c,
        found: r.data.found,
        product_id: r.data.product?.id,
        warehouse_id: warehouseId,
      }, { withCredentials: true }).then(() => loadHistory()).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка сканирования");
    }
    setScanning(false);
    setCode("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && code.trim()) {
      e.preventDefault();
      doLookup(code);
    }
  };

  // ---------- Camera mode ----------
  useEffect(() => {
    let mounted = true;
    if (mode !== "camera") {
      // Cleanup camera on switch back to USB
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch { /* noop */ }
        scannerRef.current = null;
      }
      return;
    }
    // Dynamic import so html5-qrcode is only loaded when needed
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!mounted) return;
        const scanner = new Html5Qrcode(cameraDivId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 140 }, aspectRatio: 1.777 },
          (decodedText) => {
            if (!mounted) return;
            // Stop after first successful scan; user can rescan by clicking button.
            scanner.pause(true);
            doLookup(decodedText);
          },
          () => { /* frame-level errors → ignore */ }
        );
      } catch (e) {
        alert("Не удалось запустить камеру: " + (e?.message || e));
        setMode("usb");
      }
    })();
    return () => {
      mounted = false;
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(() => {}); } catch { /* noop */ }
        scannerRef.current = null;
      }
    };
  }, [mode]);

  const resumeCamera = async () => {
    if (mode === "camera" && scannerRef.current) {
      try { await scannerRef.current.resume(); } catch { /* noop */ }
    }
  };

  return (
    <div data-testid="admin-inventory-audit" style={{ padding: "20px", maxWidth: "960px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", fontWeight: 400, margin: 0 }}>Инвентаризация</h1>
          <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#8a8a89", marginTop: "2px" }}>
            Сканируйте этикетку — карточка товара подтянется автоматически.
          </div>
        </div>

        {/* Warehouse selector */}
        <select data-testid="audit-warehouse-select" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
          style={{ padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "6px", fontFamily: "Manrope", fontSize: "13px", background: "white" }}>
          <option value="">— Склад не выбран —</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} · {w.code}</option>)}
        </select>
      </div>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <ModeBtn active={mode === "usb"} onClick={() => setMode("usb")}
          icon={<Keyboard size={14} />} label="USB-сканер" testid="mode-usb-btn" />
        <ModeBtn active={mode === "camera"} onClick={() => setMode("camera")}
          icon={<Camera size={14} />} label="Камера" testid="mode-camera-btn" />
      </div>

      {/* Scan surface */}
      {mode === "usb" ? (
        <div style={{ background: "linear-gradient(135deg, #0A0A0A, #1a1a1a)", borderRadius: "12px", padding: "24px", border: "1px solid rgba(197,160,89,0.25)" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "rgba(197,160,89,0.7)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Scan size={14} /> Наведите сканер на этикетку
          </div>
          <input ref={inputRef} data-testid="audit-scan-input" value={code}
            onChange={(e) => setCode(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="MC-01-26 · 2110086000004 · …"
            autoFocus autoComplete="off"
            style={{ width: "100%", background: "transparent", border: "none", borderBottom: "2px solid rgba(197,160,89,0.4)",
                     padding: "12px 0", fontFamily: "monospace", fontSize: "26px", fontWeight: 600,
                     color: "#D4B878", letterSpacing: "0.06em", outline: "none" }} />
          <div style={{ marginTop: "8px", fontFamily: "Manrope", fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>
            {scanning ? "Ищу…" : "Ожидаю скан или ручной ввод (нажмите Enter)"}
          </div>
        </div>
      ) : (
        <div style={{ background: "#0A0A0A", borderRadius: "12px", padding: "16px", border: "1px solid rgba(197,160,89,0.25)" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "rgba(197,160,89,0.7)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Camera size={14} /> Наведите камеру на этикетку
          </div>
          <div id={cameraDivId} style={{ width: "100%", minHeight: "260px", background: "#000", borderRadius: "8px", overflow: "hidden" }} />
          <button onClick={resumeCamera} style={{ marginTop: "10px", background: "rgba(197,160,89,0.15)", color: "#D4B878", border: "1px solid rgba(197,160,89,0.4)", borderRadius: "4px", padding: "8px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Сканировать следующий
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ marginTop: "20px" }}>
          {result.found
            ? <FoundCard result={result} onClose={() => setResult(null)} />
            : <NotFoundCard code={result.code} onClose={() => setResult(null)} />}
        </div>
      )}

      {/* Recent history */}
      <div style={{ marginTop: "28px" }}>
        <h3 style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "10px" }}>
          Последние сканирования
        </h3>
        <div style={{ display: "grid", gap: "6px" }}>
          {history.length === 0 && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#a3a3a2" }}>Пусто — ещё нет сканирований.</div>}
          {history.map((s) => (
            <div key={s.id} data-testid="scan-history-row"
              style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", background: s.found ? "#f0fdf4" : "#fef2f2",
                       border: `1px solid ${s.found ? "#bbf7d0" : "#fecaca"}`, borderRadius: "6px",
                       fontFamily: "Manrope", fontSize: "12.5px" }}>
              {s.found ? <CheckCircle2 size={14} style={{ color: "#16a34a", flexShrink: 0 }} /> : <AlertCircle size={14} style={{ color: "#dc2626", flexShrink: 0 }} />}
              <span style={{ fontFamily: "monospace", fontWeight: 700, color: s.found ? "#14532d" : "#7f1d1d", flex: "0 0 140px" }}>{s.code}</span>
              <span style={{ color: "#374151", flex: 1 }}>
                {s.found ? (
                  s.product_id ? (
                    <ProductHoverPreview productId={s.product_id} fallbackName="найдено" fallbackSku={s.code}>
                      найдено
                    </ProductHoverPreview>
                  ) : "найдено"
                ) : "не найдено в системе"}
              </span>
              <span style={{ color: "#6b7280", fontSize: "11px" }}>{s.scanned_by_name || s.scanned_by || "?"} · {formatTime(s.scanned_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────── Sub-components ────────── */

function ModeBtn({ active, onClick, icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ background: active ? "linear-gradient(135deg,#C5A059,#D4B878)" : "white",
               color: active ? "#0A0A0A" : "#374151",
               border: `1px solid ${active ? "#C5A059" : "#e5e7eb"}`,
               borderRadius: "6px", padding: "8px 14px", cursor: "pointer",
               display: "flex", alignItems: "center", gap: "6px",
               fontFamily: "Manrope", fontSize: "12px", fontWeight: active ? 700 : 500 }}>
      {icon}{label}
    </button>
  );
}

function FoundCard({ result, onClose }) {
  const p = result.product;
  const img = (p.images && p.images[0]) || null;
  return (
    <div data-testid="scan-result-found"
      style={{ background: "white", border: "2px solid #16a34a", borderRadius: "12px", padding: "18px",
               display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "18px", alignItems: "start" }}>
      <div style={{ width: "120px", height: "140px", background: "#f5f5f4", borderRadius: "8px",
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {img
          ? <img src={img} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#a3a3a2" }}>нет фото</div>}
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <CheckCircle2 size={16} style={{ color: "#16a34a" }} />
          <span style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#16a34a" }}>Найдено</span>
        </div>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 500, lineHeight: 1.2 }}>{p.name}</div>
        <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
          {p.brand ? <><b>{p.brand}</b> · </> : null}{p.category}
        </div>
        <div style={{ display: "flex", gap: "18px", marginTop: "10px", fontFamily: "Manrope", fontSize: "12px" }}>
          {p.our_sku && <span>Наш SKU: <b style={{ fontFamily: "monospace" }}>{p.our_sku}</b></span>}
          {p.sku && <span>Штрихкод: <b style={{ fontFamily: "monospace" }}>{p.sku}</b></span>}
          {p.price ? <span>Цена: <b>{Number(p.price).toLocaleString("ru-RU")} ₸</b></span> : null}
        </div>

        {/* Stock breakdown */}
        <div style={{ marginTop: "12px", background: "#fafaf9", border: "1px solid #eee", borderRadius: "8px", padding: "10px 12px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>
            Остаток: <span style={{ color: result.total_qty > 0 ? "#16a34a" : "#dc2626" }}>{result.total_qty} шт</span>
          </div>
          {result.stock && result.stock.length > 0 ? (
            <div style={{ display: "grid", gap: "4px" }}>
              {result.stock.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontFamily: "Manrope", fontSize: "12px" }}>
                  <span style={{ color: "#374151" }}>{s.warehouse_label} · размер {s.size || "—"}</span>
                  <span style={{ fontWeight: 700 }}>{s.qty_on_hand} шт</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#a3a3a2", fontStyle: "italic" }}>
              Нет остатков в системе. Проведите поступление через карточку товара.
            </div>
          )}
        </div>
      </div>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
    </div>
  );
}

function NotFoundCard({ code, onClose }) {
  return (
    <div data-testid="scan-result-not-found"
      style={{ background: "white", border: "2px solid #dc2626", borderRadius: "12px", padding: "20px",
               display: "flex", alignItems: "flex-start", gap: "16px" }}>
      <AlertCircle size={26} style={{ color: "#dc2626", flexShrink: 0, marginTop: "2px" }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#dc2626", marginBottom: "4px" }}>Не найдено</div>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 500 }}>Товара с кодом <span style={{ fontFamily: "monospace", color: "#dc2626" }}>{code}</span> нет в системе</div>
        <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#6b7280", marginTop: "8px", lineHeight: 1.5 }}>
          Возможно, товар ещё не добавлен в каталог, или на этикетке заводской штрихкод, который не привязан к нашей карточке.
        </div>
        <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={() => alert("Откройте раздел «Каталог» → «Добавить товар» и вставьте этот код в поле «Артикул завода / штрихкод».")}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "4px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
            <Plus size={14} /> Создать товар с этим кодом
          </button>
        </div>
      </div>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}
